import { useEffect, useRef } from 'react';

export default function CTA() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-cta').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 200);
            });
          }
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative py-28 lg:py-40 overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0D1B2A 0%, #0F0F0F 60%, #0D1B2A 100%)' }}
    >
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20dark%20luxury%20background%20with%20subtle%20golden%20geometric%20patterns%2C%20premium%20legal%20document%20texture%20overlay%2C%20sophisticated%20dark%20navy%20and%20black%20gradient%20with%20delicate%20gold%20foil%20accents%2C%20minimalist%20high-end%20atmosphere%2C%20bokeh%20effect%2C%20smooth%20cinematic%20composition&width=1920&height=700&seq=cta01&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top opacity-15"
        />
      </div>
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-navy/80 via-deep/90 to-navy/80" />

      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />

      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12 text-center">
        {/* Tag */}
        <div
          className="fade-cta inline-flex items-center gap-3 mb-8"
          style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}
        >
          <span className="w-8 h-px bg-gold/60" />
          <span className="text-gold font-sans font-600 text-xs tracking-[0.25em] uppercase">Consulta Jurídica</span>
          <span className="w-8 h-px bg-gold/60" />
        </div>

        {/* Title */}
        <h2
          className="fade-cta font-serif text-ice text-4xl lg:text-6xl font-700 leading-tight mb-6"
          style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s' }}
        >
          Precisa de Assessoria<br />
          <span className="text-gold italic">Jurídica Especializada?</span>
        </h2>

        {/* Divider */}
        <div
          className="fade-cta flex items-center justify-center gap-4 my-8"
          style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.8s ease 0.3s, transform 0.8s ease 0.3s' }}
        >
          <span className="w-16 h-px bg-gold/40" />
          <span className="w-2 h-2 rounded-full bg-gold/60" />
          <span className="w-16 h-px bg-gold/40" />
        </div>

        {/* Description */}
        <p
          className="fade-cta font-sans text-ice/65 text-lg font-300 leading-relaxed max-w-2xl mx-auto mb-12"
          style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.8s ease 0.4s, transform 0.8s ease 0.4s' }}
        >
          Entre em contato agora e agende uma consulta. Nossa equipe está pronta para analisar sua situação com total sigilo, ética e comprometimento. O primeiro passo para a sua defesa começa aqui.
        </p>

        {/* CTA Button */}
        <div
          className="fade-cta flex flex-col items-center gap-4"
          style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.8s ease 0.5s, transform 0.8s ease 0.5s' }}
        >
          <a
            href="https://wa.me/5598981578332?text=Olá,%20gostaria%20de%20agendar%20uma%20consulta%20jurídica."
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="inline-flex items-center gap-3 bg-gold hover:bg-gold-light text-deep font-sans font-600 text-lg px-10 py-5 rounded-md transition-all duration-300 whitespace-nowrap cursor-pointer"
            style={{ boxShadow: '0 0 48px rgba(200,162,74,0.35)' }}
          >
            <i className="ri-whatsapp-line text-2xl" />
            Agendar Consulta Jurídica
          </a>
          <div className="flex items-center gap-2 text-ice/40 font-sans text-sm">
            <i className="ri-time-line text-gold/60" />
            <span>Resposta em até 2 horas úteis</span>
          </div>
        </div>

        {/* Trust Badges */}
        <div
          className="fade-cta flex flex-wrap items-center justify-center gap-8 mt-16 pt-12 border-t border-gold/10"
          style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.8s ease 0.6s, transform 0.8s ease 0.6s' }}
        >
          {['Sigilo Garantido', 'OAB/MA Registrado', 'Atendimento Humanizado', 'Estratégia Personalizada'].map((badge) => (
            <div key={badge} className="flex items-center gap-2 text-ice/40">
              <i className="ri-checkbox-circle-line text-gold/60 text-sm" />
              <span className="font-sans text-xs font-300 tracking-wider">{badge}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
